import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { IColumnConfig } from '@/types/admin/dataTable';
import { IResidentTableRow } from '@/types/admin/residentList';
import { SVG_COMPONENTS } from '@/assets/admin';
import { capitalizeFirstLetter } from '@/utils/admin/utils';
import { ADMIN_COLORS } from '@/constants/admin';
import { getLeaseStatusColor } from '@/utils/admin/residentsList/status';
import { formatDateShort } from '@/utils/admin/date';

// Column configuration for resident data table
export const residentTableColumns: IColumnConfig<IResidentTableRow>[] = [
  // NAME
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    filterable: false, // handled by global search
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    renderCell: (value: string) => (
      <Text style={styles.nameText} numberOfLines={1}>
        {value}
      </Text>
    ),
  },
  // LEASE STATUS
  {
    key: 'lease_status',
    label: 'Lease Status',
    sortable: true,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    filterType: 'multiSelect',
    filterOptions: [
      { label: 'Current', value: 'current' },
      { label: 'Notice', value: 'notice' },
      { label: 'Future', value: 'future' },
      { label: 'Past', value: 'past' },
      { label: 'Cancelled', value: 'cancelled' },
    ],
    renderCell: (value: string) => (
      <View style={styles.statusBadge}>
        <Text style={[styles.statusText, { color: getLeaseStatusColor(value) }]}> 
          {capitalizeFirstLetter(value)}
        </Text>
      </View>
    ),
    renderMobileCell: (value: string) => (
      <View style={styles.statusBadge}>
        <Text style={styles.autopayText}>Lease Status: </Text>
        <Text style={[styles.statusText, { color: getLeaseStatusColor(value) }]}> 
          {capitalizeFirstLetter(value)}
        </Text>
      </View>
    ),
  },
  // UNIT
  {
    key: 'unit',
    label: 'Unit',
    sortable: true,
    filterable: true, // admin-web allows string filter
    visible: true,
    mobileVisible: false,
    sortType: 'string',
    filterType: 'text',
    renderCell: (value: string) => (
      <Text style={styles.infoText}>{value || 'N/A'}</Text>
    ),
  },
  // LAST LOGIN (date)
  {
    key: 'last_login',
    label: 'Last Login',
    sortable: true,
    filterable: true, // date filters supported in admin-web
    visible: true,
    mobileVisible: false,
    sortType: 'date',
    filterType: 'date',
    renderCell: (value: Date | null) => (
      <Text style={styles.infoText}>{formatDateShort(value)}</Text>
    ),
  },
  // LAST RESIO PAYMENT (date)
  {
    key: 'last_payment',
    label: 'Last Resio Payment',
    sortable: true,
    filterable: true, // date filters supported in admin-web
    visible: true,
    mobileVisible: false,
    sortType: 'date',
    filterType: 'date',
    renderCell: (value: Date | null) => (
      <Text style={styles.infoText}>{formatDateShort(value)}</Text>
    ),
  },
  // CUSTOMER TYPE
  {
    key: 'customer_type',
    label: 'Customer Type',
    sortable: true,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'string',
    filterType: 'select',
    filterOptions: [
      { label: 'Resident', value: 'resident' },
      { label: 'Guarantor', value: 'guarantor' },
      { label: 'Occupant', value: 'occupant' },
    ],
    renderCell: (value: string) => (
      <Text style={styles.customerTypeText} numberOfLines={1}>
        {capitalizeFirstLetter(value)}
      </Text>
    ),
  },
  // AUTOPAY (boolean, not sortable in admin-web)
  {
    key: 'autopay',
    label: 'Autopay',
    sortable: false,
    filterable: true,
    visible: true,
    mobileVisible: true,
    sortType: 'boolean',
    filterType: 'select',
    filterOptions: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
    renderCell: (value: boolean) => (
      <View style={[styles.autopayContainer, styles.autopayCenter]}>
        {value ? (
          <SVG_COMPONENTS.CHECK_CIRCLE width={20} height={20} color="#4CAF50" />
        ) : (
          <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color="#F44336" />
        )}
      </View>
    ),
    renderMobileCell: (value: boolean) => (
      <View style={styles.autopayContainer}>
        <Text style={styles.autopayText}>Autopay: </Text>
        {value ? (
          <SVG_COMPONENTS.CHECK_CIRCLE width={12} height={12} color="#4CAF50" />
        ) : (
          <SVG_COMPONENTS.X_CIRCLE width={12} height={12} color="#F44336" />
        )}
      </View>
    ),
  },
];

const styles = StyleSheet.create({
  nameText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
  customerTypeText: {
    fontSize: 16,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
  statusBadge: {
    paddingVertical: 2,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusText: {
    fontSize: 16,
    fontWeight: '500',
  },
  autopayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  autopayCenter: {
    alignSelf: 'center',
  },
  autopayText: {
    fontSize: 16,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
  infoText: {
    fontSize: 16,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
  },
});
